/**
 * useAIChatStream — AG-UI hook for the Forecast AI chat panel.
 *
 * Uses @ag-ui/client HttpAgent for the full AG-UI event lifecycle.
 * Gateway URL and auth header are configured via constants at the top of
 * this file. The hook owns all message + streaming state.
 */
import {useState, useCallback, useRef, useEffect, useLayoutEffect} from 'react';
import type {Dispatch, SetStateAction} from 'react';
import {commitLocalUpdate} from 'react-relay';
import {ConnectionHandler} from 'relay-runtime';
import {HttpAgent} from '@ag-ui/client';
import type {RunAgentInput} from '@ag-ui/client';
import {v4 as uuid} from 'uuid';
import type {ActionPlan, ActionResult, EntityType, EntitySubtype} from './actionCardTypes';
import {parseActionPlan} from './actionCardTypes';
import RelayEnvironment from '../../RelayEnvironment';

/**
 * The shape POSTed to the gateway's /actions/{action_id}/resolve endpoint.
 * Mirrors gateway/actions.py:ResolveRequest in forecast-ai-agents — KEEP IN
 * SYNC. Wire contract documented in
 * DevEnvironment/forecast-ai-agents/docs/superpowers/specs/2026-06-23-tnsr-247-hitl-rework-design.md.
 *
 * `overrides` mirrors the planned gateway override field (cross-repo contract;
 * backend acceptance is a separate ticket). It is forwarded today and ignored
 * by the current backend; honoured once the backend round-trip ticket lands.
 */
export type ResolveRequest = {
	actionId: string;
	approved: boolean;
	entity_type: EntityType;
	entity_subtype: EntitySubtype;
	/** Entity IDs the user deselected; FE computes as full affected minus selected. */
	exclude_ids: number[];
	thread_id: string;
	/** Per-entity field edits keyed by entity ID. Absent when no edits were made. */
	overrides?: Record<string, Record<string, unknown>>;
};

// HttpAgent does not expose a credentials option, so we subclass to add it.
class CredentialedHttpAgent extends HttpAgent {
	protected override requestInit(input: RunAgentInput): RequestInit {
		return {...super.requestInit(input), credentials: 'include'};
	}
}

// Process is provided by webpack DefinePlugin at build time
declare const process: {
	env: {
		REACT_APP_AI_DEPLOY_VERSION: string;
		REACT_APP_AI_GATEWAY_URL?: string;
	};
};

const GATEWAY = process.env.REACT_APP_AI_GATEWAY_URL || 'http://localhost:7861';
const FORECAST_CHAT_AGENT_ID = 'forecast_chat';
const USER_APPROVAL_TOOL = 'user_approval_tool';

const SESSION_MESSAGES_KEY = 'ai-chat-messages';
const SESSION_THREAD_KEY = 'ai-chat-thread-id';
const SESSION_DEPLOY_KEY = 'ai-chat-deploy-version';

// Clear stale sessionStorage state when a new deployment is detected.
// REACT_APP_AI_DEPLOY_VERSION is the CI commit hash (empty in local dev).
// An empty version means we can't detect deployments — skip the check.
const DEPLOY_VERSION = process.env.REACT_APP_AI_DEPLOY_VERSION || '';

if (DEPLOY_VERSION) {
	try {
		const stored = sessionStorage.getItem(SESSION_DEPLOY_KEY);
		if (stored !== DEPLOY_VERSION) {
			sessionStorage.removeItem(SESSION_MESSAGES_KEY);
			sessionStorage.removeItem(SESSION_THREAD_KEY);
			sessionStorage.setItem(SESSION_DEPLOY_KEY, DEPLOY_VERSION);
		}
	} catch (_) {
		// no-op — sessionStorage may be unavailable
	}
}

// ── Domain types ──────────────────────────────────────────────────────────────

export type StatusStep = {
	id: string;
	stepType: 'thinking' | 'collating' | 'tool';
	done: boolean;
	toolName?: string;
	input?: Record<string, unknown>;
	output?: string;
	outputTruncated?: boolean;
};

export type Message = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	pending?: boolean;
	statusSteps?: StatusStep[];
	createdAt?: string;
	error?: string;
	actionPlan?: ActionPlan;
	actionPaused?: boolean;
	actionResolved?: boolean;
	actionResult?: ActionResult;
	actionDismissed?: boolean;
};

type UseAIChatStreamReturn = {
	messages: Message[];
	setMessages: Dispatch<SetStateAction<Message[]>>;
	isStreaming: boolean;
	isHitlPending: boolean;
	sendMessage: (userText: string, allMessages: Message[]) => Promise<void>;
	abort: () => void;
	clearMessages: () => void;
	resetThread: () => void;
	respondToAction: (
		actionId: string,
		decision: {approved: boolean; selectedIds: string[]; overrides?: Record<string, Record<string, unknown>>}
	) => Promise<ActionResult>;
};

// ── Local AG-UI subscriber types ──────────────────────────────────────────────
// Mirrors the relevant subset of AgentSubscriber from @ag-ui/client.
// Defined locally so TypeScript can infer handler parameter types without
// requiring the IDE to resolve @ag-ui/client from node_modules/.ignored/.

type StepEvt = {stepName: string};
type ToolCallStartEvt = {toolCallId: string; toolCallName: string};
type ToolCallEndEvt = {toolCallId: string};
type ToolCallResultEvt = {toolCallId: string; content: string};
type TextMessageContentEvt = {delta: string};
type RunErrorEvt = {message: string};

type AIChatSubscriber = {
	onStepStartedEvent?: (params: {event: StepEvt}) => void;
	onStepFinishedEvent?: (params: {event: StepEvt}) => void;
	onToolCallStartEvent?: (params: {event: ToolCallStartEvt}) => void;
	onToolCallEndEvent?: (params: {event: ToolCallEndEvt; toolCallArgs: Record<string, unknown>}) => void;
	onToolCallResultEvent?: (params: {event: ToolCallResultEvt}) => void;
	onTextMessageStartEvent?: () => void;
	onTextMessageContentEvent?: (params: {event: TextMessageContentEvt}) => void;
	onRunFinishedEvent?: () => void;
	onRunErrorEvent?: (params: {event: RunErrorEvt}) => void;
	onRunFailed?: (params: {error: Error}) => void;
};

// ── Persistence helpers ───────────────────────────────────────────────────────

function readPersistedMessages(): Message[] {
	try {
		const raw = sessionStorage.getItem(SESSION_MESSAGES_KEY);
		return raw ? (JSON.parse(raw) as Message[]) : [];
	} catch (_) {
		return [];
	}
}

function readOrCreateThreadId(): string {
	try {
		const stored = sessionStorage.getItem(SESSION_THREAD_KEY);
		if (stored) return stored;
		const id = uuid();
		sessionStorage.setItem(SESSION_THREAD_KEY, id);
		return id;
	} catch (_) {
		return uuid();
	}
}

// ── Tool label lookup ─────────────────────────────────────────────────────────

// Tools emitted by the gateway that are internal signals — never shown in UI.
enum SentinelTool {
	CreateProjectIntent = 'create_project_intent',
}
const SENTINEL_TOOLS: Set<string> = new Set(Object.values(SentinelTool));

// ── Default resolveAction transport ──────────────────────────────────────────
//
// POSTs `req` to the gateway's /actions/{actionId}/resolve endpoint with
// the same auth headers as the SSE stream. Used when the caller of
// useAIChatStream doesn't supply a custom `resolveAction` prop. Lives at
// module scope (not in a closure) so tests can import + assert on it.
//
// Wire contract documented in:
//   forecast-ai-agents/docs/superpowers/specs/2026-06-23-tnsr-247-hitl-rework-design.md
//   forecast-ai-agents/gateway/actions.py:resolve_action

export type ResolveTransportAuth = {
	apiKey: string;
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
};

export async function defaultResolveActionTransport(req: ResolveRequest, auth: ResolveTransportAuth): Promise<ActionResult> {
	const headers: Record<string, string> = {'Content-Type': 'application/json'};
	if (auth.apiKey) headers['X-Forecast-Api-Key'] = auth.apiKey;
	if (auth.viewerPersonId != null) headers['X-Viewer-Person-Id'] = String(auth.viewerPersonId);
	if (auth.companyId) headers['X-Forecast-Company-Id'] = auth.companyId;
	if (auth.companyName) headers['X-Forecast-Company-Name'] = auth.companyName;
	const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
	if (tz) headers['X-User-Timezone'] = tz;

	const url = `${GATEWAY}/actions/${encodeURIComponent(req.actionId)}/resolve`;

	let resp: Response;
	try {
		resp = await fetch(url, {
			method: 'POST',
			credentials: 'include',
			headers,
			body: JSON.stringify({
				approved: req.approved,
				entity_type: req.entity_type,
				entity_subtype: req.entity_subtype,
				exclude_ids: req.exclude_ids,
				thread_id: req.thread_id,
				overrides: req.overrides,
			}),
		});
	} catch (e) {
		return {status: 'failed', guard: 'transport', message: e instanceof Error ? e.message : 'Network error'};
	}

	// Treat 401/403 as transport failures so the FE can show Retry; the
	// auth layer above will refresh credentials on the next attempt.
	if (!resp.ok) {
		let detail = '';
		try {
			const j = await resp.json();
			detail = typeof j?.detail === 'string' ? j.detail : JSON.stringify(j);
		} catch {
			detail = `HTTP ${resp.status}`;
		}
		return {status: 'failed', guard: 'transport', message: detail};
	}

	try {
		const parsed = (await resp.json()) as ActionResult;
		return parsed;
	} catch (e) {
		return {status: 'failed', guard: 'transport', message: e instanceof Error ? e.message : 'Malformed response'};
	}
}

// ── Relay store helpers ───────────────────────────────────────────────────────

export type DeleteProjectResult = {
	/** Relay global ID (base64 node ID) of the deleted project. */
	relayNodeId: string;
	/** customProjectId slug (e.g. "P-42") read from the Relay store before deletion — used to compare against the URL. undefined when the record was not found in the store. */
	customProjectId: string | undefined;
};

/**
 * Removes a deleted project from the Relay viewer connections and returns its IDs.
 * Called after the MCP delete_project tool succeeds so the project list updates
 * immediately without a page reload. Mirrors the RANGE_DELETE configs in
 * delete_project_mutation.js (must be kept in sync if new connection keys are added).
 *
 * Returns undefined when content indicates no deletion (deleted !== true, missing
 * id, bad JSON, or non-string project_id). The relayNodeId must be the base64
 * Relay node ID — a plain integer companyProjectId won't match any Relay node.
 */
export function applyDeleteProjectToRelayStore(content: string, fallbackProjectId?: string): DeleteProjectResult | undefined {
	let parsed: Record<string, unknown>;
	try {
		parsed = JSON.parse(content) as Record<string, unknown>;
	} catch {
		return undefined;
	}
	const rawId = parsed.project_id ?? fallbackProjectId;
	const projectId = typeof rawId === 'string' ? rawId : undefined;
	if (parsed.deleted !== true || !projectId) return undefined;
	let customProjectId: string | undefined;
	commitLocalUpdate(RelayEnvironment.getInstance(), store => {
		const record = store.get(projectId);
		customProjectId = record?.getValue('customProjectId') as string | undefined;
		const viewer = store.getRoot().getLinkedRecord('viewer');
		if (!viewer) return;
		const conn = ConnectionHandler.getConnection(viewer, 'Viewer_projects');
		if (conn) ConnectionHandler.deleteNode(conn, projectId);
		const headerConn = ConnectionHandler.getConnection(viewer, 'Header_Viewer_projects');
		if (headerConn) ConnectionHandler.deleteNode(headerConn, projectId);
	});
	return {relayNodeId: projectId, customProjectId};
}

// ── Action card pure helpers ──────────────────────────────────────────────────

export function pauseUnresolvedActions(msgs: Message[]): Message[] {
	return msgs.map(m => (m.actionPlan && !m.actionResolved ? {...m, actionPaused: true} : m));
}

export function applyActionResult(msgs: Message[], actionId: string, result: ActionResult): Message[] {
	return msgs.map(m => (m.actionPlan?.action_id === actionId ? {...m, actionResolved: true, actionResult: result} : m));
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAIChatStream({
	apiKey,
	viewerPersonId,
	companyId,
	companyName,
	onProjectIntent,
	onProjectDeleted,
	resolveAction,
}: {
	apiKey: string;
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
	onProjectIntent?: (data: Record<string, unknown>) => void;
	/** Called after delete_project succeeds. projectId is the Relay node ID; customProjectId is the URL slug (e.g. "P-42") read from the store — compare against the URL to navigate away only from the deleted project's page. */
	onProjectDeleted?: (projectId: string, customProjectId?: string) => void;
	resolveAction?: (req: ResolveRequest) => Promise<ActionResult>;
}): UseAIChatStreamReturn {
	const [messages, setMessages] = useState<Message[]>(readPersistedMessages);
	const [isStreaming, setIsStreaming] = useState(false);
	const [isHitlPending, setIsHitlPending] = useState(false);
	const agentRef = useRef<HttpAgent | null>(null);
	const stepsRef = useRef<StatusStep[]>([]);
	const prevApiKeyRef = useRef<string | null>(null);
	const viewerPersonIdRef = useRef<number | undefined>(viewerPersonId);
	viewerPersonIdRef.current = viewerPersonId;
	const companyIdRef = useRef<string | undefined>(companyId);
	companyIdRef.current = companyId;
	const companyNameRef = useRef<string | undefined>(companyName);
	companyNameRef.current = companyName;
	const onProjectIntentRef = useRef<((data: Record<string, unknown>) => void) | undefined>(onProjectIntent);
	onProjectIntentRef.current = onProjectIntent;
	const onProjectDeletedRef = useRef<((projectId: string, customProjectId?: string) => void) | undefined>(onProjectDeleted);
	onProjectDeletedRef.current = onProjectDeleted;
	const resolveActionRef = useRef<typeof resolveAction>(resolveAction);
	resolveActionRef.current = resolveAction;
	// Mirror of the latest messages so respondToAction (useCallback []) can read
	// the current ActionPlan without re-creating the callback or abusing setMessages.
	const messagesRef = useRef<Message[]>(messages);
	messagesRef.current = messages;

	// (Re-)init the HttpAgent when apiKey first becomes available or changes.
	// useLayoutEffect (synchronous, pre-paint) preserves the timing guarantee
	// of the previous render-phase init without being a side-effect-in-render.
	useLayoutEffect(() => {
		if (agentRef.current && (!apiKey || apiKey === prevApiKeyRef.current)) return;
		prevApiKeyRef.current = apiKey;
		const headers: Record<string, string> = {};
		if (apiKey) headers['X-Forecast-Api-Key'] = apiKey;
		if (viewerPersonIdRef.current != null) headers['X-Viewer-Person-Id'] = String(viewerPersonIdRef.current);
		// Tenant headers for telemetry. Omit entirely when unavailable —
		// the gateway treats absent and empty headers differently.
		if (companyIdRef.current) headers['X-Forecast-Company-Id'] = companyIdRef.current;
		if (companyNameRef.current) headers['X-Forecast-Company-Name'] = companyNameRef.current;
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (userTimeZone) headers['X-User-Timezone'] = userTimeZone;
		headers['x-agent-id'] = FORECAST_CHAT_AGENT_ID;
		agentRef.current = new CredentialedHttpAgent({
			url: `${GATEWAY}/invocations`,
			threadId: agentRef.current ? agentRef.current.threadId : readOrCreateThreadId(),
			headers,
		});
	}, [apiKey]);

	useEffect(() => {
		try {
			const toStore = messages.filter((m: Message) => !m.pending);
			sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(toStore));
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
	}, [messages]);

	// ── Step state helpers (sync ref mutations, no re-render until syncSteps) ──
	function resetSteps(): void {
		stepsRef.current = [];
	}
	function pushStep(step: Omit<StatusStep, 'id' | 'done'>): string {
		const id = uuid();
		stepsRef.current = [...stepsRef.current, {id, done: false, ...step}];
		return id;
	}
	function updateStep(id: string, patch: Partial<StatusStep>): void {
		stepsRef.current = stepsRef.current.map((s: StatusStep) => (s.id === id ? {...s, ...patch} : s));
	}
	function doneAll(): void {
		stepsRef.current = stepsRef.current.map((s: StatusStep) => ({...s, done: true}));
	}
	function syncSteps(msgId: string): void {
		const snapshot = [...stepsRef.current];
		setMessages((prev: Message[]) => prev.map((m: Message) => (m.id === msgId ? {...m, statusSteps: snapshot} : m)));
	}

	// Rebuild the AG-UI agent bound to a fresh thread_id. A new thread starts the
	// LangGraph checkpointer from empty, so any pending/expired HITL interrupt on
	// the previous thread is abandoned rather than resumed.
	const rebuildAgent = useCallback((newThreadId: string): void => {
		try {
			sessionStorage.setItem(SESSION_THREAD_KEY, newThreadId);
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
		const headers: Record<string, string> = {};
		const key = prevApiKeyRef.current;
		if (key) headers['X-Forecast-Api-Key'] = key;
		if (viewerPersonIdRef.current != null) headers['X-Viewer-Person-Id'] = String(viewerPersonIdRef.current);
		if (companyIdRef.current) headers['X-Forecast-Company-Id'] = companyIdRef.current;
		if (companyNameRef.current) headers['X-Forecast-Company-Name'] = companyNameRef.current;
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (userTimeZone) headers['X-User-Timezone'] = userTimeZone;
		headers['x-agent-id'] = FORECAST_CHAT_AGENT_ID;
		agentRef.current = new CredentialedHttpAgent({
			url: `${GATEWAY}/invocations`,
			threadId: newThreadId,
			headers,
		});
	}, []);

	// ── Reset thread (keep transcript) ─────────────────────────────────────────
	// Abort any in-flight stream, reset flags, and start a fresh thread. Does NOT
	// wipe the visible transcript. Used by regenerate so the resent query runs on
	// a clean thread and a stale (e.g. expired) pending action isn't carried
	// forward by the agent. rebuildAgent re-persists SESSION_THREAD_KEY.
	const resetThread = useCallback((): void => {
		if (agentRef.current) agentRef.current.abortRun();
		setIsStreaming(false);
		setIsHitlPending(false);
		rebuildAgent(uuid());
	}, [rebuildAgent]);

	// ── Clear ─────────────────────────────────────────────────────────────────
	const clearMessages = useCallback(() => {
		try {
			sessionStorage.removeItem(SESSION_MESSAGES_KEY);
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
		setMessages([]);
		// resetThread aborts the stream, resets flags, and re-inits the agent on a
		// fresh thread so LLM context is also reset.
		resetThread();
	}, [resetThread]);

	// ── Abort ─────────────────────────────────────────────────────────────────
	const abort = useCallback(() => {
		if (agentRef.current) agentRef.current.abortRun();
	}, []);

	// ── Respond to an approval action card ─────────────────────────────────────
	//
	// The card calls this with `{approved, selectedIds}`. We translate that
	// into the gateway's resolve contract:
	//   - look up the message's ActionPlan for entity_type / entity_subtype
	//     and the full affected list
	//   - compute `exclude_ids` = (full_affected_ids - selectedIds), coerced
	//     to integers (MCP expects list[int]). For Confirm with no
	//     deselection, exclude_ids is empty.
	//   - pre-flight `expires_at`: if the plan has already expired on the
	//     client clock, short-circuit to ActionResult.status='expired'
	//     without round-tripping (TNSR-247 design note).
	//   - read thread_id from the HttpAgent so /actions/resolve can mutate
	//     the same LangGraph thread.
	const respondToAction = useCallback(
		async (
			actionId: string,
			decision: {approved: boolean; selectedIds: string[]; overrides?: Record<string, Record<string, unknown>>}
		): Promise<ActionResult> => {
			let result: ActionResult;
			try {
				// Read the current ActionPlan from the messages ref. Lookup is
				// best-effort; if the message/plan is missing for any reason,
				// fail closed with a clear error so the FE knows.
				const plan = messagesRef.current.find(m => m.actionPlan?.action_id === actionId)?.actionPlan;
				if (!plan) {
					return {status: 'failed', message: 'Action plan not found in chat state'};
				}

				// Client-side expires_at pre-flight. The server returns the
				// 'expired' status when MCP rejects on TTL miss; this just
				// avoids the round-trip when we already know it's gone.
				if (plan.expires_at) {
					const expiresMs = Date.parse(plan.expires_at);
					if (Number.isFinite(expiresMs) && expiresMs < Date.now()) {
						result = {
							status: 'expired',
							guard: 'expired',
							message: 'This plan has expired. Please ask Nova for a fresh plan.',
						};
						setMessages((prev: Message[]) => applyActionResult(prev, actionId, result));
						return result;
					}
				}

				// Compute exclude_ids = (all affected − user's selection). Use each
				// item's originalId (the pre-dedup server id) rather than the React-key
				// `id`, which parseActionPlan suffixes on collisions ('10234-2'). Parse
				// strictly: a whole-number id becomes an int; a non-numeric id is
				// dropped + logged rather than silently partial-parsed (Number.parseInt
				// turned '12abc' into 12 and '10234-2' into 10234). De-dup so genuinely
				// duplicated entities don't emit the same int twice.
				const selectedSet = new Set(decision.selectedIds);
				const excludeSet = new Set<number>();
				if (decision.approved) {
					for (const item of plan.affected) {
						if (selectedSet.has(item.id)) continue;
						const serverId = item.originalId ?? item.id;
						if (/^\d+$/.test(serverId)) {
							excludeSet.add(Number(serverId));
						} else {
							console.warn('[respondToAction] non-numeric affected id, cannot build exclude_ids', serverId);
						}
					}
				}
				const excludeInts = [...excludeSet];

				const threadId = agentRef.current?.threadId ?? '';
				const req: ResolveRequest = {
					actionId,
					approved: decision.approved,
					entity_type: plan.entity_type,
					entity_subtype: plan.entity_subtype,
					exclude_ids: excludeInts,
					thread_id: threadId,
					overrides: decision.overrides,
				};

				// Use a caller-provided transport if one was passed (tests
				// stub it for unit coverage). Otherwise fall back to the
				// default fetch() transport that POSTs the same gateway as
				// the SSE stream with the auth headers the HttpAgent uses.
				if (resolveActionRef.current) {
					result = await resolveActionRef.current(req);
				} else {
					result = await defaultResolveActionTransport(req, {
						apiKey: prevApiKeyRef.current ?? '',
						viewerPersonId: viewerPersonIdRef.current,
						companyId: companyIdRef.current,
						companyName: companyNameRef.current,
					});
				}
			} catch (e) {
				result = {status: 'failed', message: e instanceof Error ? e.message : 'failed'};
			}
			// Persist the resolution. A rejection (approved:false) also marks the
			// message dismissed so a rehydrated cancelled card stays "Cancelled."
			// instead of restoring as Failed+Retry from the gateway's result.
			setMessages((prev: Message[]) => {
				const applied = applyActionResult(prev, actionId, result);
				if (decision.approved) return applied;
				return applied.map(m => (m.actionPlan?.action_id === actionId ? {...m, actionDismissed: true} : m));
			});
			return result;
		},
		[]
	);

	// ── Send ──────────────────────────────────────────────────────────────────
	const sendMessage = useCallback(
		async (userText: string, allMessages: Message[]): Promise<void> => {
			if (isStreaming) return;

			setIsStreaming(true);
			resetSteps();

			const assistantMsgId = uuid();
			setMessages((prev: Message[]) => [
				...pauseUnresolvedActions(prev),
				{
					id: uuid(),
					role: 'user',
					content: userText,
					createdAt: new Date().toISOString(),
				},
				{
					id: assistantMsgId,
					role: 'assistant',
					content: '',
					pending: true,
					statusSteps: [],
					createdAt: new Date().toISOString(),
				},
			]);

			let thinkingId: string | null = null;
			const toolCallStepMap = new Map<string, string>(); // toolCallId → stepId
			const sentinelToolCallMap = new Map<string, string>(); // toolCallId → toolName (for sentinel tools)
			const approvalToolCallIds = new Set<string>(); // toolCallIds for user_approval_tool (no visible step)
			const toolCallNameMap = new Map<string, string>(); // toolCallId → toolName (for Relay store invalidation after mutations)
			const toolCallArgsMap = new Map<string, Record<string, unknown>>(); // toolCallId → toolCallArgs (for extracting IDs on mutation results)
			let textStarted = false;
			let finished = false;

			const finish = (patch: Partial<Message> = {}): void => {
				if (finished) return;
				finished = true;
				doneAll();
				syncSteps(assistantMsgId);
				setIsHitlPending(false);
				setMessages((prev: Message[]) =>
					prev.map((m: Message) => (m.id === assistantMsgId ? {...m, pending: false, ...patch} : m))
				);
				setIsStreaming(false);
			};

			const agent = agentRef.current;
			if (!agent) {
				finish({error: 'Agent not initialised'});
				return;
			}

			agent.setMessages([
				...allMessages
					.filter((m: Message) => !m.pending)
					.map((m: Message) => ({id: m.id, role: m.role, content: m.content})),
				{id: uuid(), role: 'user', content: userText},
			] as Parameters<HttpAgent['setMessages']>[0]);

			const subscriber: AIChatSubscriber = {
				onStepStartedEvent({event}: {event: StepEvt}) {
					const stepName = event && event.stepName;
					if (stepName === 'call_model' && !thinkingId) {
						thinkingId = pushStep({stepType: 'thinking'});
						syncSteps(assistantMsgId);
					}
				},
				onStepFinishedEvent({event}: {event: StepEvt}) {
					const stepName = event && event.stepName;
					if (stepName === 'call_model' && thinkingId) {
						updateStep(thinkingId, {done: true});
						thinkingId = null;
						syncSteps(assistantMsgId);
					}
				},
				onToolCallStartEvent({event}: {event: ToolCallStartEvt}) {
					if (event.toolCallName === USER_APPROVAL_TOOL) {
						setIsHitlPending(true);
						approvalToolCallIds.add(event.toolCallId);
						return;
					}
					if (SENTINEL_TOOLS.has(event.toolCallName)) {
						sentinelToolCallMap.set(event.toolCallId, event.toolCallName);
						return;
					}
					// Drop any preamble the model streamed before deciding to call a
					// tool (e.g. "I'll fetch the health of all running projects"). The
					// real answer is produced after tools return; without this the
					// preamble stays concatenated in front of the final answer and
					// persists in the bubble.
					setMessages((prev: Message[]) =>
						prev.map((m: Message) => (m.id === assistantMsgId ? {...m, content: ''} : m))
					);
					if (thinkingId) {
						updateStep(thinkingId, {done: true});
						thinkingId = null;
					}
					const toolName = event.toolCallName;
					const stepId = pushStep({stepType: 'tool', toolName});
					toolCallStepMap.set(event.toolCallId, stepId);
					toolCallNameMap.set(event.toolCallId, toolName);
					syncSteps(assistantMsgId);
				},
				onToolCallEndEvent({event, toolCallArgs}: {event: ToolCallEndEvt; toolCallArgs: Record<string, unknown>}) {
					if (approvalToolCallIds.has(event.toolCallId)) {
						const plan = parseActionPlan(toolCallArgs);
						if (plan) {
							setMessages((prev: Message[]) =>
								prev.map((m: Message) => (m.id === assistantMsgId ? {...m, actionPlan: plan} : m))
							);
						}
						return;
					}
					const stepId = toolCallStepMap.get(event.toolCallId);
					if (stepId) {
						updateStep(stepId, {input: toolCallArgs});
						syncSteps(assistantMsgId);
					}
					toolCallArgsMap.set(event.toolCallId, toolCallArgs);
				},
				onToolCallResultEvent({event}: {event: ToolCallResultEvt}) {
					const sentinelName = sentinelToolCallMap.get(event.toolCallId);
					if (sentinelName) {
						sentinelToolCallMap.delete(event.toolCallId);
						if (sentinelName === SentinelTool.CreateProjectIntent && onProjectIntentRef.current) {
							try {
								const data = JSON.parse(event.content) as Record<string, unknown>;
								onProjectIntentRef.current(data);
							} catch {
								// ignore parse errors — intent still fired, modal opens without prefill
							}
						}
						return;
					}
					const stepId = toolCallStepMap.get(event.toolCallId);
					if (stepId) {
						const content = event.content;
						updateStep(stepId, {
							done: true,
							output: content,
							outputTruncated: content.includes('[... output truncated at'),
						});
						toolCallStepMap.delete(event.toolCallId);
						syncSteps(assistantMsgId);
					}
					const mutatingToolName = toolCallNameMap.get(event.toolCallId);
					const mutatingToolArgs = toolCallArgsMap.get(event.toolCallId);
					toolCallNameMap.delete(event.toolCallId);
					toolCallArgsMap.delete(event.toolCallId);
					if (mutatingToolName === 'delete_project') {
						const rawFallback = mutatingToolArgs?.project_id;
						const result = applyDeleteProjectToRelayStore(
							event.content,
							typeof rawFallback === 'string' ? rawFallback : undefined
						);
						if (result) {
							onProjectDeletedRef.current?.(result.relayNodeId, result.customProjectId);
						}
					}
				},
				onTextMessageStartEvent() {
					doneAll();
					const colId = pushStep({stepType: 'collating'});
					updateStep(colId, {done: true});
					syncSteps(assistantMsgId);
				},
				onTextMessageContentEvent({event}: {event: TextMessageContentEvt}) {
					if (!textStarted) {
						textStarted = true;
						if (!stepsRef.current.some((s: StatusStep) => s.stepType === 'collating')) {
							doneAll();
							const colId = pushStep({stepType: 'collating'});
							updateStep(colId, {done: true});
							syncSteps(assistantMsgId);
						}
					}
					const delta = event.delta || '';
					setMessages((prev: Message[]) =>
						prev.map((m: Message) =>
							m.id === assistantMsgId ? {...m, content: m.content + delta, pending: false} : m
						)
					);
				},
				onRunFinishedEvent() {
					finish();
				},
				onRunErrorEvent({event}: {event: RunErrorEvt}) {
					finish({error: event.message || 'An error occurred'});
				},
				onRunFailed({error}: {error: Error}) {
					if (error.name === 'AbortError') {
						finish({error: 'stopped'});
					} else {
						finish({error: error.message || 'Connection error'});
					}
				},
			};

			try {
				await agent.runAgent(
					{forwardedProps: {agent_id: FORECAST_CHAT_AGENT_ID}},
					subscriber as Parameters<HttpAgent['runAgent']>[1]
				);
			} catch (err) {
				if (err instanceof Error && err.name === 'AbortError') {
					finish({error: 'stopped'});
				} else {
					finish({error: err instanceof Error ? err.message : 'Connection error'});
				}
			} finally {
				setIsStreaming(false);
			}
		},
		[isStreaming, apiKey]
	);

	return {messages, setMessages, isStreaming, isHitlPending, sendMessage, abort, clearMessages, resetThread, respondToAction};
}
